import { Search } from "lucide-react";
import TailwindSideModal from "../../../../components/commonModal/TailwindSideModal";
import TailwindBottomButtonView from "../../../../components/mainBottomButtonView/TailwindBottomButtonView";
import { Checkbox } from "../../../../components/ui/common/checkbox";
import { Input } from "../../../../components/ui/common/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "../../../../components/ui/common/table";
import CommonSplitContainer from "../../../../components/ui/layout/CommonSplitContainer/CommonSplitContainer";

export interface AllowListItem {
  value: string | number;
  text: string;
  /** Set when this item is already allowed for the company (the "Added on" date). */
  createdAt?: string;
}

interface AllowListModalProps {
  visibleModal: boolean;
  title: string;
  nameColumnLabel: string;
  searchPlaceholder: string;
  items: AllowListItem[];
  checkedValues: Array<string | number>;
  onToggle: (value: string | number) => void;
  searchValue: string;
  onSearchChange: (value: string) => void;
  isSaving: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

/** Same "YYYY-MM-DD HH:mm:ss" (UTC) text the legacy tables showed. */
const formatAddedOn = (value?: string) => {
  const date = value ? new Date(value) : null;
  if (!date || isNaN(date.getTime())) return "";
  return date.toISOString().slice(0, 19).replace("T", " ");
};

/**
 * Shared Tailwind shell for the two "which items may this company use"
 * pickers (recipe ingredients, generic processes) — a searchable
 * checkbox table with an "Added on" column and Cancel / Allow actions, on the
 * same `TailwindSideModal` + `CommonSplitContainer` shell as the other LCA
 * drawers. Replaces the two near-identical Semantic UI modals
 * (src/screens/company/recipeIngredient, .../genericProcessModal).
 */
const AllowListModal = ({
  visibleModal,
  title,
  nameColumnLabel,
  searchPlaceholder,
  items,
  checkedValues,
  onToggle,
  searchValue,
  onSearchChange,
  isSaving,
  onConfirm,
  onClose
}: AllowListModalProps) => {
  return (
    <TailwindSideModal
      open={visibleModal}
      onOpenChange={open => {
        if (!open) onClose();
      }}
      eyebrow="Companies"
      title={title}
      titleClassName="tw-font-display"
    >
      <CommonSplitContainer
        currentHeight={0.9}
        overflowX="hidden"
        topChild={
          <div className="tw-flex tw-flex-col tw-gap-4 tw-bg-muted/20 tw-p-6">
            <div className="tw-flex tw-items-center tw-justify-between tw-gap-4">
              <div className="tw-relative tw-w-full sm:tw-w-72">
                <Search
                  size={16}
                  className="tw-pointer-events-none tw-absolute tw-left-3 tw-top-1/2 -tw-translate-y-1/2 tw-text-muted-foreground"
                />
                <Input
                  value={searchValue}
                  onChange={e => onSearchChange(e.target.value)}
                  placeholder={searchPlaceholder}
                  className="tw-bg-white tw-pl-9"
                />
              </div>
              <p className="tw-shrink-0 tw-text-xs tw-text-muted-foreground">
                {checkedValues.length} selected
              </p>
            </div>

            <div className="tw-overflow-hidden tw-rounded-lg tw-border tw-border-border tw-bg-white">
              <Table className="tw-w-full [&_td]:tw-px-4 [&_td]:tw-py-2.5 [&_th]:tw-px-4 [&_th]:tw-py-3">
                <TableHeader>
                  <TableRow className="tw-border-b-0 tw-bg-primary/5 hover:tw-bg-primary/5 [&_th]:tw-bg-primary/5 [&_th]:tw-text-primary [&_th]:tw-font-semibold">
                    <TableHead>{nameColumnLabel}</TableHead>
                    <TableHead className="tw-w-[14rem]">Added on</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.length === 0 ? (
                    <TableRow className="tw-bg-white hover:tw-bg-white">
                      <TableCell
                        colSpan={2}
                        className="tw-py-10 tw-text-center tw-text-muted-foreground"
                      >
                        No results found.
                      </TableCell>
                    </TableRow>
                  ) : (
                    items.map(item => (
                      <TableRow key={item.value} className="tw-bg-white hover:tw-bg-muted/50">
                        <TableCell>
                          <label className="tw-flex tw-cursor-pointer tw-items-center tw-gap-3 tw-text-sm tw-text-foreground">
                            <Checkbox
                              checked={checkedValues.includes(item.value)}
                              onCheckedChange={() => onToggle(item.value)}
                            />
                            {item.text}
                          </label>
                        </TableCell>
                        <TableCell className="tw-font-mono tw-text-xs tw-text-muted-foreground">
                          {formatAddedOn(item.createdAt)}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        }
        bottomChild={
          <TailwindBottomButtonView
            cancelButtonStatus
            saveButtonStatus
            saveTitle="Allow"
            buttonSize="lg"
            saveLoading={isSaving}
            saveDisable={isSaving}
            cancelButton={onClose}
            saveButton={onConfirm}
          />
        }
      />
    </TailwindSideModal>
  );
};

export default AllowListModal;
